import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import { gerarRelatorioPDF } from "@/lib/report";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileDown, FileSpreadsheet, Factory, Loader2 } from "lucide-react";
import { toast } from "sonner";

const KEY = "nexolog_prod_unit";
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const fmt = (v, d = 3) => (v === null || v === undefined || v === "" ? "—" : Number(v).toLocaleString("pt-BR", { maximumFractionDigits: d }));
export const money = (v) => (v === null || v === undefined || v === "" ? "—" : Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL", minimumFractionDigits: 2, maximumFractionDigits: 4 }));
export const money2 = (v) => (v === null || v === undefined || v === "" ? "—" : Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL", minimumFractionDigits: 2, maximumFractionDigits: 2 }));
export const today = () => new Date().toISOString().slice(0, 10);
export const addDays = (s, n) => { const d = new Date(s + "T12:00:00"); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
export const weekStart = (s) => { const d = new Date(s + "T12:00:00"); const w = (d.getDay() + 6) % 7; return addDays(s, -w); };
export const DIAS = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];
export const fmtData = (s) => (s ? s.slice(0, 10).split("-").reverse().join("/") : "—");
export const errMsg = (e) => { const d = e.response?.data?.detail; return typeof d === "string" ? d : d?.msg || "Erro"; };
export const newKey = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : String(Date.now() + Math.random()));

export function useRoles() {
  const { user } = useAuth();
  const admin = ["SUPER_ADMIN", "MASTER"].includes(user.role);
  return { user, tec: admin || ["GESTOR", "SUPERVISOR"].includes(user.role), aprov: admin || user.role === "GESTOR" };
}

export function useFabrica() {
  const [unidades, setUnidades] = useState(null);
  const [unitId, setUnit] = useState(localStorage.getItem(KEY) || "");
  useEffect(() => {
    api.get("/producao/unidades").then((r) => {
      setUnidades(r.data);
      if (r.data.length && !r.data.find((u) => u.id === localStorage.getItem(KEY))) { setUnit(r.data[0].id); localStorage.setItem(KEY, r.data[0].id); }
    }).catch(() => setUnidades([]));
  }, []);
  const setUnitId = (v) => { setUnit(v); localStorage.setItem(KEY, v); };
  const valid = unidades?.find((u) => u.id === unitId) ? unitId : "";
  return { unidades, unitId: valid, setUnitId, unidade: unidades?.find((u) => u.id === valid) };
}

export function ProdHeader({ title, icon: Icon = Factory, fab, children }) {
  return (
    <div className="flex items-end justify-between flex-wrap gap-3">
      <div>
        <div className="text-xs text-primary uppercase tracking-[0.18em] font-bold">Produção</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold uppercase flex items-center gap-2"><Icon className="w-8 h-8 text-primary" /> {title}</h1>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {fab && fab.unidades?.length > 0 && (
          <Select value={fab.unitId} onValueChange={fab.setUnitId}>
            <SelectTrigger className="w-56" data-testid="prod-unit-select"><SelectValue placeholder="Fábrica" /></SelectTrigger>
            <SelectContent>{fab.unidades.map((u) => <SelectItem key={u.id} value={u.id}>{u.codigo} — {u.nome}</SelectItem>)}</SelectContent>
          </Select>
        )}
        {children}
      </div>
    </div>
  );
}

export function FabricaGate({ fab, children }) {
  if (!fab.unidades) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  if (!fab.unidades.length) return (
    <Card className="p-10 text-center text-muted-foreground" data-testid="no-fabrica">
      Nenhuma unidade marcada como <b className="text-foreground">fábrica</b> no seu escopo. Peça ao Gestor/Master para habilitar em <Link to="/unidades" className="text-primary underline">Cadastros → Unidades</Link>.
    </Card>
  );
  return fab.unitId ? children : null;
}

const STATUS_CLS = {
  PLANEJADA: "bg-secondary text-muted-foreground", LIBERADA: "bg-sky-500/15 text-sky-300", EM_PRODUCAO: "bg-amber-500/15 text-amber-300",
  CONCLUIDA: "bg-emerald-500/15 text-emerald-300", CANCELADA: "bg-red-500/15 text-red-300", PENDENTE: "bg-secondary text-muted-foreground",
  PARCIAL: "bg-amber-500/15 text-amber-300", ATENDIDA: "bg-emerald-500/15 text-emerald-300", ENCERRADA: "bg-zinc-500/20 text-zinc-300",
  APROVADA: "bg-sky-500/15 text-sky-300", COMPRADA: "bg-violet-500/15 text-violet-300", RECEBIDA: "bg-emerald-500/15 text-emerald-300",
  RASCUNHO: "bg-secondary text-muted-foreground", OBSOLETA: "bg-zinc-500/20 text-zinc-400", ENCERRANDO: "bg-amber-500/15 text-amber-300",
};
const STATUS_LBL = { EM_PRODUCAO: "Em produção", PENDENTE: "Pendente", PARCIAL: "Parcial", COMPRADA: "Comprada", PLANEJADA: "Planejada", LIBERADA: "Liberada", CONCLUIDA: "Concluída", CANCELADA: "Cancelada", ATENDIDA: "Atendida", ENCERRADA: "Encerrada", APROVADA: "Aprovada", RECEBIDA: "Recebida", RASCUNHO: "Rascunho", OBSOLETA: "Obsoleta" };

export function StatusBadge({ s, testid }) {
  return <span data-testid={testid} className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded-sm font-mono whitespace-nowrap ${STATUS_CLS[s] || "bg-secondary"}`}>{STATUS_LBL[s] || s}</span>;
}

export async function exportPDF({ titulo, colunas, linhas, filtros = [], orientacao = "paisagem" }) {
  let empresa = {}, logoUrl = null;
  try {
    empresa = (await api.get("/config/empresa")).data;
    if (empresa.logo_path) logoUrl = `${BACKEND}/api/files/${empresa.logo_path}?auth=${localStorage.getItem("nexolog_token")}`;
  } catch { /* sem dados da empresa */ }
  gerarRelatorioPDF({ empresa, logoUrl, titulo, colunas, linhas, filtros, orientacao });
}

export async function exportXLSX({ titulo, colunas, linhas }) {
  try {
    const r = await api.post("/producao/export/xlsx", { titulo, colunas, linhas }, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = `${titulo.replace(/[^\w-]+/g, "_")}.xlsx`; a.click();
    URL.revokeObjectURL(url);
  } catch { toast.error("Falha ao exportar Excel"); }
}

export function ExportBar({ titulo, colunas, linhas, filtros, testid = "export" }) {
  const dis = !linhas?.length;
  return (
    <div className="flex gap-2">
      <Button variant="outline" size="sm" disabled={dis} onClick={() => exportPDF({ titulo, colunas, linhas, filtros })} data-testid={`${testid}-pdf`}><FileDown className="w-4 h-4" /> PDF / Imprimir</Button>
      <Button variant="outline" size="sm" disabled={dis} onClick={() => exportXLSX({ titulo, colunas, linhas })} data-testid={`${testid}-xlsx`}><FileSpreadsheet className="w-4 h-4" /> Excel</Button>
    </div>
  );
}

export function DataTable({ colunas, linhas, testid, empty = "Sem dados.", render = {} }) {
  return (
    <div className="overflow-x-auto border border-border rounded-md" data-testid={testid}>
      <table className="w-full text-sm">
        <thead className="bg-secondary/60"><tr>{colunas.map((c) => <th key={c.key} className={`px-3 py-2 text-[10px] uppercase tracking-wide font-bold text-muted-foreground whitespace-nowrap ${c.align === "right" ? "text-right" : "text-left"}`}>{c.label}</th>)}</tr></thead>
        <tbody>
          {linhas.map((r, i) => (
            <tr key={r.id || i} className="border-t border-border hover:bg-secondary/30 transition-colors">
              {colunas.map((c) => <td key={c.key} className={`px-3 py-2 ${c.align === "right" ? "text-right font-mono" : ""}`}>{render[c.key] ? render[c.key](r) : (typeof r[c.key] === "number" ? fmt(r[c.key]) : (r[c.key] ?? "—"))}</td>)}
            </tr>
          ))}
          {!linhas.length && <tr><td colSpan={colunas.length} className="px-3 py-8 text-center text-muted-foreground">{empty}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

export { API };
